import React from "react";
import TextInput from "../../UI/TextInput";
import { companyPluginUseCase } from "../../../../di/container";
import type { LiveConsultChannelType } from "../../../../domain/types/company-plugin";
import type { CompanyPluginDialogProps } from "./types";
import { PluginDialogFrame } from "./PluginDialogFrame";
import { resolveErrorMessage } from "../../../pages/CompanyAdmin/MallDesign/utils";

const CHANNEL_OPTIONS: {
  value: LiveConsultChannelType;
  name: string;
  iconSrc: string;
}[] = [
  { value: "KAKAO_CHANNEL", name: "카카오 채널", iconSrc: "/assets/icons/kakao-icon.svg" },
  { value: "CHANNEL_TALK", name: "채널톡", iconSrc: "/assets/logo/channel-talk.png" },
];

/**
 * 실시간 상담 플러그인 등록/수정.
 *
 * 채널은 택1이다 — 쇼핑몰 우측 하단 플로팅 상담 버튼이 하나뿐이다.
 * 쇼핑몰에 실제로 띄울지는 쇼핑몰 디자인 > 상담 설정의 ON/OFF 가 정한다.
 */
export const LiveConsultPluginDialog = ({
  companyId,
  initialConfig,
  onClose,
  onSaved,
  onDeleted,
}: CompanyPluginDialogProps<"LIVE_CONSULT">) => {
  const [channel, setChannel] = React.useState<LiveConsultChannelType>(
    initialConfig?.consultChannelType ?? "KAKAO_CHANNEL",
  );
  // 채널을 바꿨다 돌아와도 입력값이 남도록 채널별로 따로 들고 있는다. 저장은 선택한 채널 값만 보낸다.
  const [kakaoChannelUrl, setKakaoChannelUrl] = React.useState(
    initialConfig?.kakaoChannelUrl ?? "",
  );
  const [channelTalkKey, setChannelTalkKey] = React.useState(
    initialConfig?.channelTalkKey ?? "",
  );
  const [isBusy, setIsBusy] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const validate = (): string | null => {
    if (channel === "KAKAO_CHANNEL") {
      const url = kakaoChannelUrl.trim();
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        return "카카오 채널 링크를 http:// 또는 https:// 로 시작하도록 입력해 주세요.";
      }
    } else if (!channelTalkKey.trim()) {
      return "채널톡 플러그인 키를 입력해 주세요.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const invalid = validate();
    if (invalid) {
      setErrorMessage(invalid);
      return;
    }

    setIsBusy(true);
    setErrorMessage(null);
    try {
      const saved = await companyPluginUseCase.savePlugin(
        companyId,
        "LIVE_CONSULT",
        channel === "KAKAO_CHANNEL"
          ? { consultChannelType: channel, kakaoChannelUrl: kakaoChannelUrl.trim() }
          : { consultChannelType: channel, channelTalkKey: channelTalkKey.trim() },
      );
      onSaved(saved);
    } catch (error) {
      setErrorMessage(resolveErrorMessage(error, "실시간 상담 연동 저장에 실패했습니다."));
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async () => {
    setIsBusy(true);
    setErrorMessage(null);
    try {
      await companyPluginUseCase.deletePlugin(companyId, "LIVE_CONSULT");
      onDeleted("LIVE_CONSULT");
    } catch (error) {
      setErrorMessage(resolveErrorMessage(error, "연동 해제에 실패했습니다."));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <PluginDialogFrame
      title="실시간 상담"
      isEditing={initialConfig !== null}
      isBusy={isBusy}
      errorMessage={errorMessage}
      onClose={onClose}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
    >
      <div className="text-[#555] mb-[0.625rem]">상담 채널</div>
      <div role="radiogroup" className="grid grid-cols-2 gap-[0.5rem] mb-[1.25rem]">
        {CHANNEL_OPTIONS.map((option) => {
          const isSelected = channel === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => {
                setChannel(option.value);
                setErrorMessage(null);
              }}
              className={`flex items-center gap-[0.5rem] px-[0.875rem] py-[0.75rem] rounded-[8px] border transition-all
                ${isSelected ? "border-[#4285F4] ring-2 ring-[#4285F4]/20 bg-[#E8EFFF]" : "border-[#e0e0e0] bg-white hover:bg-[#f5f5f5]"}`}
            >
              <img src={option.iconSrc} alt="" className="w-[24px] h-[24px] rounded-[6px]" />
              <span className="font-medium text-[#222]">{option.name}</span>
            </button>
          );
        })}
      </div>

      {channel === "KAKAO_CHANNEL" ? (
        <>
          <div className="text-[#555] mb-[0.625rem]">카카오 채널 링크</div>
          <TextInput
            innerClassName="bg-gray-50"
            value={kakaoChannelUrl}
            onChange={(e) => setKakaoChannelUrl(e.target.value)}
            placeholder="https://pf.kakao.com/_xxxxx"
          />
          <ul className="text-[0.75rem] text-[#757575] mt-2">
            <li>· 쇼핑몰 상담 버튼을 누르면 이 링크가 새 탭으로 열립니다.</li>
            <li>
              · 푸터 SNS 줄의 카카오 채널 로고는 쇼핑몰 디자인 &gt; SNS 링크에서 따로 설정합니다.
            </li>
          </ul>
        </>
      ) : (
        <>
          <div className="text-[#555] mb-[0.625rem]">채널톡 플러그인 키</div>
          <TextInput
            innerClassName="bg-gray-50"
            value={channelTalkKey}
            onChange={(e) => setChannelTalkKey(e.target.value)}
            placeholder="채널톡 플러그인 키를 입력해 주세요."
          />
          <ul className="text-[0.75rem] text-[#757575] mt-2">
            <li>· 채널톡 관리자 &gt; 채널 설정 &gt; 일반 설정 &gt; 버튼 설치 및 설정에서 확인할 수 있습니다.</li>
          </ul>
        </>
      )}
    </PluginDialogFrame>
  );
};
